// ─── src/GewPartnersView.tsx ─────────────────────────────────────────────────
import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Download, Trash2, Search, X, Handshake, Building2, Eye, CheckCircle2 } from "lucide-react";
import { useGewPartners, useGewPartnerStats, useDeleteGewPartner } from "./hooks/useGew";
import { Pagination } from "./components/dashboard/Pagination";
import { TableSkeleton } from "./components/ui/Skeleton";
import { StatCard } from "./components/ui/StatCard";
import type { GewPartner } from "./api/gew";

const LIMIT = 20;

function exportCsv(rows: GewPartner[]) {
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = [
    "Org Name (AR)", "Org Name (EN)", "Org Type", "Org Type (other)",
    "Org City", "Org Links",
    "Prior Collaboration", "Previous Collaboration Details",
    "Coordinator Name", "Coordinator Role", "Email", "Phone",
    "Authorized",
    "Participation Scope", "Expected Cities", "Participation Description",
    "Venue Status", "Venue Description",
    "Involve Others", "Other Parties Details",
    "Commitments Accepted", "Submitted At",
  ].join(",");
  const body = rows.map((r) =>
    [
      r.orgNameAr, r.orgNameEn ?? "", r.orgType, r.orgTypeOther ?? "",
      r.orgCity, r.orgLinks ?? "",
      r.hasCollaboratedBefore ? "Yes" : "No", r.previousCollaboration ?? "",
      r.coordinatorName, r.coordinatorRole, r.email, r.coordinatorPhone,
      r.isAuthorized,
      r.participationScope, r.expectedCities ?? "", r.participationDescription,
      r.venueStatus, r.venueDescription ?? "",
      r.willInvolveOthers ? "Yes" : "No", r.otherPartiesDetails ?? "",
      `${r.commitments?.length ?? 0}/3`, r.createdAt,
    ].map(esc).join(",")
  );
  const blob = new Blob(["﻿" + [header, ...body].join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `gew_partners_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function GewPartnersView() {
  const [search, setSearch] = useState("");
  const [orgType, setOrgType] = useState("");
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<GewPartner | null>(null);

  const { data: stats } = useGewPartnerStats();
  const { data: allData } = useGewPartners({ page: 1, limit: 2000 });
  const { data: tableData, isLoading, isFetching, isError } =
    useGewPartners({ page, limit: LIMIT, search, orgType });

  const { mutate: deleteRow, isPending: isDeleting } = useDeleteGewPartner();

  // Inline expand: several rows can be open at once so the reviewer
  // can scan and compare without jumping in and out of a modal. The
  // legacy Eye-modal button stays available for anyone who prefers it.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleRow = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const rows = tableData?.data ?? [];
  const meta = tableData?.meta ?? { total: 0, page: 1, limit: LIMIT, totalPages: 1 };
  const orgTypeOptions = Object.keys(stats?.byOrgType ?? {}).sort();

  const allExpanded = useMemo(
    () => rows.length > 0 && rows.every((r) => expanded.has(r.id)),
    [rows, expanded],
  );
  const expandAll = () => setExpanded(new Set(rows.map((r) => r.id)));
  const collapseAll = () => setExpanded(new Set());

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total inquiries" value={stats?.total ?? 0} icon={Handshake} accent="bg-brand-orange" />
        <StatCard label="Prior collaborators" value={stats?.priorCollaborators ?? 0} icon={CheckCircle2} accent="bg-brand-green" />
        <StatCard label="Org types" value={orgTypeOptions.length} icon={Building2} accent="bg-brand-teal" />
        <StatCard label="Scopes tracked" value={Object.keys(stats?.byParticipationScope ?? {}).length} icon={Handshake} accent="bg-brand-teal" />
      </div>

      <div className="card p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
          <input
            type="text" value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search org, coordinator or email…"
            className="w-full pl-9 pr-8 py-2 text-sm bg-brand-navy border border-brand-border rounded-lg text-white placeholder-brand-muted focus:outline-none focus:border-brand-green"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted hover:text-white">
              <X size={14} />
            </button>
          )}
        </div>
        <select
          value={orgType}
          onChange={(e) => { setOrgType(e.target.value); setPage(1); }}
          className="py-2 px-3 text-sm bg-brand-navy border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green"
        >
          <option value="">All org types</option>
          {orgTypeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button
          onClick={allExpanded ? collapseAll : expandAll}
          disabled={rows.length === 0}
          className="ml-auto text-sm flex items-center gap-1.5 px-3 py-2 rounded-lg border border-brand-border text-brand-muted hover:text-white hover:border-brand-green disabled:opacity-50"
          title={allExpanded ? "Collapse all rows" : "Expand all rows"}
        >
          {allExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          {allExpanded ? "Collapse all" : "Expand all"}
        </button>
        <button
          onClick={() => exportCsv(allData?.data ?? [])}
          disabled={(allData?.data ?? []).length === 0}
          className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <div className="card p-8 text-center text-red-400">Failed to load partners.</div>
      ) : rows.length === 0 ? (
        <div className="card p-10 text-center text-brand-muted">No partner inquiries yet.</div>
      ) : (
        <>
          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-brand-border text-left text-brand-muted">
                  <th className="px-2 py-3 font-medium w-6"></th>
                  <th className="px-4 py-3 font-medium">#</th>
                  <th className="px-4 py-3 font-medium">Organization</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Coordinator</th>
                  <th className="px-4 py-3 font-medium">Scope</th>
                  <th className="px-4 py-3 font-medium">City</th>
                  <th className="px-4 py-3 font-medium">Submitted</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const isOpen = expanded.has(r.id);
                  return (
                    <React.Fragment key={r.id}>
                      <tr
                        className={`border-b border-brand-border/50 hover:bg-brand-border/20 transition-colors cursor-pointer ${isOpen ? "bg-brand-border/10" : ""}`}
                        onClick={() => toggleRow(r.id)}
                      >
                        <td className="px-2 py-3 text-brand-muted">
                          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </td>
                        <td className="px-4 py-3 text-brand-muted">{(meta.page - 1) * LIMIT + i + 1}</td>
                        <td className="px-4 py-3 text-white" dir="rtl">
                          <div>{r.orgNameAr}</div>
                          {r.orgNameEn && <div className="text-xs text-brand-muted" dir="ltr">{r.orgNameEn}</div>}
                        </td>
                        <td className="px-4 py-3 text-brand-muted" dir="rtl">
                          {r.orgType === "غير ذلك" && r.orgTypeOther ? r.orgTypeOther : r.orgType}
                        </td>
                        <td className="px-4 py-3 text-brand-muted">
                          <div dir="rtl">{r.coordinatorName}</div>
                          <div className="text-xs" dir="ltr">{r.email}</div>
                        </td>
                        <td className="px-4 py-3 text-brand-muted max-w-xs truncate" dir="rtl" title={r.participationScope}>
                          {r.participationScope}
                        </td>
                        <td className="px-4 py-3 text-brand-muted" dir="rtl">{r.orgCity}</td>
                        <td className="px-4 py-3 text-brand-muted whitespace-nowrap">
                          {new Date(r.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <button onClick={() => setDetail(r)} className="p-1.5 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border" title="View details in modal">
                              <Eye size={14} />
                            </button>
                            <button onClick={() => deleteRow(r.id)} disabled={isDeleting} className="p-1.5 rounded-lg text-brand-muted hover:text-red-400 hover:bg-red-500/10 disabled:opacity-40" title="Delete">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-brand-border/50 bg-brand-navy/40">
                          <td colSpan={9} className="px-4 py-4">
                            <InlineDetail row={r} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination
            page={meta.page}
            totalPages={meta.totalPages}
            total={meta.total}
            limit={LIMIT}
            onPageChange={setPage}
            isFetching={isFetching}
          />
        </>
      )}

      {detail && <DetailModal row={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

// ── Inline row detail ────────────────────────────────────────────────────────
// Rendered directly under an expanded table row so multiple partners' full
// details can be read side-by-side without a modal. Same field set as the
// modal below, laid out more compactly.
function InlineDetail({ row }: { row: GewPartner }) {
  return (
    <div className="space-y-4 pl-6">
      <InlineSection label="القسم الأول — معلومات المؤسسة">
        <InlineField label="Org type" value={row.orgType === "غير ذلك" && row.orgTypeOther ? row.orgTypeOther : row.orgType} rtl />
        <InlineField label="City" value={row.orgCity} rtl />
        <InlineField label="Links" value={row.orgLinks ?? undefined} />
        <InlineField label="Prior collab?" value={row.hasCollaboratedBefore ? "Yes" : "No"} />
        {row.hasCollaboratedBefore && row.previousCollaboration && (
          <InlineField label="Previous collaboration" value={row.previousCollaboration} rtl block />
        )}
      </InlineSection>

      <InlineSection label="القسم الثاني — معلومات المنسق">
        <InlineField label="Name" value={row.coordinatorName} rtl />
        <InlineField label="Role" value={row.coordinatorRole} rtl />
        <InlineField label="Email" value={row.email} />
        <InlineField label="Phone" value={row.coordinatorPhone} />
        <InlineField label="Authorized" value={row.isAuthorized} rtl />
      </InlineSection>

      <InlineSection label="القسم الثالث — المشاركة المتوقعة">
        <InlineField label="Scope" value={row.participationScope} rtl />
        <InlineField label="Expected cities" value={row.expectedCities ?? undefined} rtl />
        <InlineField label="Participation description" value={row.participationDescription} rtl block />
      </InlineSection>

      <InlineSection label="القسم الرابع — المكان والقدرة">
        <InlineField label="Venue status" value={row.venueStatus} rtl />
        <InlineField label="Involves others?" value={row.willInvolveOthers ? "Yes" : "No"} />
        {row.venueDescription && <InlineField label="Venue description" value={row.venueDescription} rtl block />}
        {row.willInvolveOthers && row.otherPartiesDetails && (
          <InlineField label="Other parties" value={row.otherPartiesDetails} rtl block />
        )}
      </InlineSection>

      {row.commitments && row.commitments.length > 0 && (
        <InlineSection label={`الموافقات · ${row.commitments.length}/3`}>
          <div className="col-span-full">
            <ul className="space-y-1.5" dir="rtl">
              {row.commitments.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-white leading-relaxed">
                  <CheckCircle2 size={14} className="text-brand-green shrink-0 mt-0.5" />
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </InlineSection>
      )}
    </div>
  );
}

function InlineSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-brand-green text-xs uppercase tracking-widest border-b border-brand-border pb-1.5 mb-2" dir="rtl">
        {label}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
        {children}
      </div>
    </div>
  );
}

function InlineField({
  label, value, rtl, block,
}: { label: string; value?: string | null; rtl?: boolean; block?: boolean }) {
  if (!value) return null;
  return (
    <div className={block ? "col-span-full" : undefined}>
      <span className="text-brand-muted text-[10px] uppercase tracking-wider">{label}:</span>{" "}
      <span className="text-white text-sm whitespace-pre-wrap leading-relaxed" dir={rtl ? "rtl" : "ltr"}>
        {value}
      </span>
    </div>
  );
}

// ── Detail modal ─────────────────────────────────────────────────────────────

function DetailModal({ row, onClose }: { row: GewPartner; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="card w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-brand-border">
          <div dir="rtl">
            <p className="text-white font-bold text-lg">{row.orgNameAr}</p>
            {row.orgNameEn && <p className="text-brand-muted text-sm" dir="ltr">{row.orgNameEn}</p>}
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          <SectionHeader label="القسم الأول — معلومات المؤسسة" />
          <Grid>
            <Field label="Org type" value={row.orgType === "غير ذلك" && row.orgTypeOther ? row.orgTypeOther : row.orgType} rtl />
            <Field label="City" value={row.orgCity} rtl />
            <Field label="Links" value={row.orgLinks ?? undefined} />
            <Field label="Prior collab?" value={row.hasCollaboratedBefore ? "Yes" : "No"} />
          </Grid>
          {row.hasCollaboratedBefore && row.previousCollaboration && (
            <Field label="Previous collaboration" value={row.previousCollaboration} rtl block />
          )}

          <SectionHeader label="القسم الثاني — معلومات المنسق" />
          <Grid>
            <Field label="Name" value={row.coordinatorName} rtl />
            <Field label="Role" value={row.coordinatorRole} rtl />
            <Field label="Email" value={row.email} />
            <Field label="Phone" value={row.coordinatorPhone} />
            <Field label="Authorized" value={row.isAuthorized} rtl />
          </Grid>

          <SectionHeader label="القسم الثالث — المشاركة المتوقعة" />
          <Grid>
            <Field label="Scope" value={row.participationScope} rtl />
            <Field label="Expected cities" value={row.expectedCities ?? undefined} rtl />
          </Grid>
          <Field label="Participation description" value={row.participationDescription} rtl block />

          <SectionHeader label="القسم الرابع — المكان والقدرة" />
          <Grid>
            <Field label="Venue status" value={row.venueStatus} rtl />
            <Field label="Involves others?" value={row.willInvolveOthers ? "Yes" : "No"} />
          </Grid>
          {row.venueDescription && <Field label="Venue description" value={row.venueDescription} rtl block />}
          {row.willInvolveOthers && row.otherPartiesDetails && (
            <Field label="Other parties" value={row.otherPartiesDetails} rtl block />
          )}

          <SectionHeader label="الموافقات" />
          <div>
            <p className="text-brand-muted text-xs uppercase tracking-wider mb-2">
              Commitments accepted ({row.commitments?.length ?? 0}/3)
            </p>
            <ul className="space-y-2" dir="rtl">
              {(row.commitments ?? []).map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-white leading-relaxed">
                  <CheckCircle2 size={14} className="text-brand-green shrink-0 mt-0.5" />
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ label }: { label: string }) {
  return (
    <p className="text-brand-green text-xs uppercase tracking-widest border-b border-brand-border pb-1.5" dir="rtl">
      {label}
    </p>
  );
}
function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}
function Field({
  label, value, rtl, block,
}: { label: string; value?: string | null; rtl?: boolean; block?: boolean }) {
  if (!value) return null;
  return (
    <div className={block ? "col-span-full" : undefined}>
      <p className="text-brand-muted text-xs uppercase tracking-wider mb-1">{label}</p>
      <p className="text-white text-sm whitespace-pre-wrap leading-relaxed" dir={rtl ? "rtl" : "ltr"}>{value}</p>
    </div>
  );
}
